import { Dialog, DialogContent } from "@sentricmusic/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit } from "react-icons/fa";
import ReactTooltip from "react-tooltip";

import { useGetMyAccount, useGetMyRecordingsDetailed, useGetMySong } from "@/api/queries";
import { hasAdminRights } from "@/auth/shared";
import LoadingSmall from "@/components/LoadingSmall";
import Modal from "@/components/Modal";
import RebrandButtonLink from "@/components/RebrandButtonLink";
import Spacer from "@/components/Spacer";
// import AppState from "@/store/appstate";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import RecordingForm from "./RecordingForm";
import SongRecording from "./SongRecording";
import SongsCreateUpdate from "./SongsCreateUpdate.tsx";
import SongWriterSplit from "./SongWriterSplit";
import useSongCreationStore from "./store/songCreationStore";

function SongDetails(props) {
  const { t } = useTranslation("songs");
  const {
    row,
  } = props;
  const { original } = row;

  const isTunecore = useUIStateStore(state => state.isTunecore);

  // Get clearState function from song creation store
  const { clearState } = useSongCreationStore();

  const styles = isTunecore
    ? { textColor: "black", backgroundColor: "#00ef86" }
    : {};

  const [recordingModalIsOpen, setRecordingModalIsOpen] = useState(false);
  const [songEditModalIsOpen, setSongEditModalIsOpen] = useState(false);

  const [selectedRecordingId, setSelectedRecordingId] = useState(null);
  const [selectedSongEditId, setSelectedSongEditId] = useState(null);

  const [currentAac, setAac] = useState(null);

  useEffect(() => {
    setAac(original.artistaccountcode);
  }, [original.artistaccountcode]);

  // Clear state when edit modal closes to prevent data persistence
  useEffect(() => {
    if (!songEditModalIsOpen && selectedSongEditId) {
      // Modal was closed, clear the store state
      const timer = setTimeout(() => {
        clearState();
      }, 100); // Small delay to ensure modal fully closes

      return () => clearTimeout(timer);
    }
  }, [songEditModalIsOpen, selectedSongEditId, clearState]);

  let song = null;
  // const selectedAccount = AppState.useState(s => s.selectedAccount);
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const songTemp = useGetMySong({ aac: original.artistaccountcode || selectedAccount, wc: original.workcode });

  if (songTemp.isSuccess && songTemp.data) {
    song = songTemp.data;
  }

  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });
  const adminRights = hasAdminRights(myAccount?.data);
  const recordings = useGetMyRecordingsDetailed({ aac: original.artistaccountcode || selectedAccount, wc: original.workcode });
  useEffect(() => {
    recordings.refetch();
  }, [song, recordings]);

  let allRecordings = [];

  if (recordings.isSuccess && song) {
    const fetchedRecordings = recordings.data.recordings || [];
    const allSongRecordings = song.recordings || [];

    // Remove deleted amendments from fetched recordings
    const deletedAmendments = allSongRecordings.filter(
      s => s.recordingidentifier && s.isdeleted,
    );
    const deletedIds = new Set(
      deletedAmendments.map(r => r.recordingidentifier),
    );

    const filteredFetchedRecordings = fetchedRecordings.filter(
      r => !deletedIds.has(r.recordingidentifier),
    );

    // Turn fetched recordings into a Map for deduplication
    // Use a composite key to ensure uniqueness when recordingidentifier is null/undefined/duplicate
    const createUniqueKey = (recording, index) => {
      if (recording.recordingidentifier && recording.recordingidentifier !== "00000000-0000-0000-0000-000000000000") {
        return recording.recordingidentifier;
      }
      // Fallback to a combination of properties to create a unique identifier
      return `${recording.uicbarcode || ""}_${recording.title || ""}_${recording.performingartist || ""}_${index}`;
    };

    const recordingsMap = new Map(
      filteredFetchedRecordings.map((r, index) => [createUniqueKey(r, index), r]),
    );

    // Process valid (non-deleted) amendments and overwrite or add to the map
    allSongRecordings
      .filter(r => !r.isdeleted)
      .forEach((r, index) => {
        const formatted = {
          artistaccountcode: song.artistaccountcode,
          isdeleted: r.isdeleted,
          isrc: r.isrc,
          performingartist: r.performingartist,
          recordingamendmentidentifier: r.recordingamendmentidentifier,
          recordingidentifier: r.recordingidentifier,
          recordlabel: r.recordlabel,
          releasedate: r.releasedate,
          s3presignedurl: r.s3presignedurl,
          title: r.title,
          uicbarcode: r.uicbarcode,
          workcode: original.workcode,
        };

        // Create a unique key for this recording
        const uniqueKey = createUniqueKey(formatted, index + filteredFetchedRecordings.length);

        // Only merge with fetched recording if we have a valid recordingidentifier match
        const fetched = (formatted.recordingidentifier && formatted.recordingidentifier !== "00000000-0000-0000-0000-000000000000")
          ? filteredFetchedRecordings.find(fr => fr.recordingidentifier === formatted.recordingidentifier) ?? null
          : null;

        // Merge objects - fetched provides base data, formatted (amendment) values take priority
        // This ensures amendment s3presignedurl takes precedence over fetched recording's url
        const merged = fetched
          ? { ...fetched, ...formatted, s3presignedurl: formatted.s3presignedurl || fetched.s3presignedurl }
          : formatted;

        // Always add to map with unique key to prevent overwrites
        recordingsMap.set(uniqueKey, merged);
      });

    allRecordings = Array.from(recordingsMap.values());
  }

  if (!song) {
    return (
      <tr>
        <td colSpan="5">
          <div className="p-4">
            <LoadingSmall />
          </div>
        </td>
      </tr>
    );
  }
  return (
    <>
      <Modal openPortal={recordingModalIsOpen} closePortal={setRecordingModalIsOpen}>
        <RecordingForm aac={currentAac} id={selectedRecordingId} modal closeForm={setRecordingModalIsOpen} />
      </Modal>

      <Dialog open={songEditModalIsOpen} onOpenChange={setSongEditModalIsOpen}>
        <DialogContent className="max-w-6xl">
          <SongsCreateUpdate aac={currentAac} id={selectedSongEditId} modal closeForm={() => setSongEditModalIsOpen(false)} />
        </DialogContent>
      </Dialog>

      <tr>
        <td colSpan="7">
          <div
            className={`rounded-lg ${isTunecore ? "bg-tunecore-black text-white" : "bg-sentric-rebrand-light-grey text-old-song-sub-color"} lg:p-6 p-4 mb-2 mt-2 lg:w-screen-width-minus-gutter w-full`}
          >
            <div className="flex flex-wrap">
              <div className="flex items-center w-full lg:w-1/2">
                <h2 className={`${isTunecore ? "text-white" : "text-black"} font-bold`}>
                  {song.title}
                </h2>
                {!!song?.alternativetitles.length && (
                  <div>
                    <div
                      data-tip={song.alternativetitles
                        .map(s => s.title)
                        .join("<br />")}
                      className={`ml-2 mr-2 inline-block h-5 text-xxs py-0 px-1 rounded bg-opacity-25 ${isTunecore ? "bg-white text-white" : "bg-old-song-sub-color text-old-song-sub-color"}`}
                    >
                      {song.alternativetitles.length}
                      {" "}
                      {t("songs:alternative-titles-count")}
                    </div>
                    <ReactTooltip effect="solid" multiline {...styles} />
                  </div>
                )}
              </div>
              <div className="flex justify-end w-full lg:w-1/2">
                <RebrandButtonLink
                  onClick={() => {
                    if (adminRights) {
                      setSelectedSongEditId(song.workcode);
                      setSongEditModalIsOpen(true);
                    }
                  }}
                  id="btnSongDetailEditSong"
                >
                  <FaEdit className="inline-block mr-2" />
                  {t("songs:edit-song-link")}
                </RebrandButtonLink>
              </div>
            </div>
            {song.iswc && (
              <p className="text-xs">
                <span className="font-semi-bold">ISWC:</span>
                {song.iswc}
              </p>
            )}
            <Spacer thin />
            <div className="flex flex-wrap">
              <div className="w-full lg:w-1/2">
                <p
                  className={`uppercase text-xs ${isTunecore ? "text-white" : "font-medium text-sentric-rebrand-grey-text"}`}
                >
                  <span>
                    {t("songs:performed-by")}
                    :
                  </span>
                  {" "}
                  {song.performingartists
                    .filter(p => p.isdeleted !== true)
                    .map(p => p.performingartistname.replace(/&#44;/g, ","))
                    .join(", ")}
                </p>
              </div>
              <div className="w-full lg:w-1/2">
                <p
                  className={`uppercase text-xs ${isTunecore ? "text-white" : "font-medium text-sentric-rebrand-grey-text"}`}
                >
                  <span>
                    {t("songs:duration")}
                    :
                  </span>
                  {" "}
                  {song.durationmins}
                  {" "}
                  {t("songs:minutes")}
                  {" "}
                  {song.durationsecs}
                  {" "}
                  {t("songs:seconds")}
                </p>
              </div>
            </div>
            <Spacer thin />
            <div>
              {song.containssamples && (
                <div className={`mr-1 inline-block text-xxs py-0 px-1 rounded uppercase bg-opacity-25 ${isTunecore ? "bg-white text-white" : "bg-old-song-sub-color text-old-song-sub-color"}`}>
                  {t("songs:contains-samples")}
                </div>
              )}
              {song.isremix && (
                <div className={`mr-1 inline-block text-xxs py-0 px-1 rounded uppercase bg-opacity-25 ${isTunecore ? "bg-white text-white" : "bg-old-song-sub-color text-old-song-sub-color"}`}>
                  {t("songs:is-remix")}
                </div>
              )}
            </div>
            <Spacer />
            <h2 className={`${isTunecore ? "text-white" : "text-black"}`}>
              {t("songs:writers")}
              {" "}
              &amp;
              {" "}
              {t("songs:splits")}
            </h2>
            <div className={`${isTunecore ? "bg-tunecore-dark-element" : "bg-white"} p-4 py-2 rounded-lg mt-2`}>
              {song.writersplits.map((w, i) => (
                <SongWriterSplit
                  aac={original.artistaccountcode || selectedAccount}
                  key={w.writercode}
                  writer={w}
                  last={i === song.writersplits.length - 1}
                />
              ))}
            </div>

            <Spacer />
            <hr />
            <Spacer thin />
            <div>
              <div className="flex items-center">
                <div className="flex items-center w-full">
                  <div className="flex items-center w-full lg:w-1/2">
                    <h2 className={`${isTunecore ? "text-white" : "text-black"} font-bold text-lg`}>
                      {t("songs:recordings-subheader")}
                    </h2>
                    <div className={`ml-2 mr-2 inline-block h-5 text-xxs py-0 px-1 rounded uppercase bg-opacity-25 ${isTunecore ? "bg-white text-white" : "bg-old-song-sub-color text-old-song-sub-color"}`}>
                      {allRecordings?.length || 0}
                    </div>
                  </div>
                  <div className="flex justify-end w-full lg:w-1/2">
                    <RebrandButtonLink
                      plusIcon
                      onClick={() => {
                        if (adminRights) {
                          setSelectedRecordingId(song.workcode);
                          setRecordingModalIsOpen(true);
                        }
                      }}
                      id="btnSongDetailEditRecording"
                    >
                      {t("songs:add-new-recording-link")}
                    </RebrandButtonLink>
                  </div>
                </div>
              </div>
              <Spacer thin />
              {!recordings.isSuccess && <LoadingSmall />}
              {recordings.isSuccess && allRecordings && (
                <div className={`${isTunecore ? "bg-tunecore-dark-element" : "bg-white"} p-4 py-2 rounded-lg mt-2`}>
                  {allRecordings.map((r, i) => (
                    <SongRecording
                      key={r.recordingidentifier || i}
                      recording={r}
                      last={i === song.recordings.length - 1}
                    />
                  ))}
                  {allRecordings.length === 0 && (
                    <p>{t("songs:no-recordings")}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </td>
      </tr>
    </>
  );
}

export default SongDetails;
